// Ocean Insight Pro Mobile - Catch Report Screen
// Quick form for logging fishing catches in under 5 seconds

import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TextInput, TouchableOpacity, Alert, Image, Modal, Platform, SafeAreaView } from 'react-native';
import { 
  Card, 
  Title, 
  Paragraph, 
  useTheme, 
  Button, 
  ActivityIndicator,
  IconButton,
  Chip
} from 'react-native-paper';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import * as ImagePicker from 'expo-image-picker';
import * as MediaLibrary from 'expo-media-library';
import DateTimePicker from '@react-native-community/datetimepicker';
import { locationService } from '../services/locationService';
import { addCatchReport, setCatchReports } from '../store/fishingSlice';
import type { GeoCoordinate, FishSpecies, CatchReport } from '../types';
import type { AppDispatch, RootState } from '../store';
import { catchReportService } from '../services/catchReportService';
import { useAuth } from '../contexts/AuthContext';
import { PlatformMap } from '../components/PlatformMap';
import { waypointService } from '../services/waypointService';

// Common fish species with icons
const FISH_SPECIES = [
  { id: 'Mahi', name: 'Mahi Mahi', icon: 'fish' },
  { id: 'Tuna', name: 'Tuna', icon: 'fish' },
  { id: 'Wahoo', name: 'Wahoo', icon: 'fish' },
  { id: 'Sailfish', name: 'Sailfish', icon: 'fish' },
  { id: 'Marlin', name: 'Marlin', icon: 'fish' },
  { id: 'Kingfish', name: 'King Fish', icon: 'fish' },
  { id: 'Snapper', name: 'Snapper', icon: 'fish' },
  { id: 'Grouper', name: 'Grouper', icon: 'fish' },
  { id: 'Other', name: 'Other', icon: 'help' },
] as const;

// Quick location options
const QUICK_LOCATIONS = [
  { id: 'photo', name: 'From Photo', icon: 'camera' },
  { id: 'map', name: 'Select on Map', icon: 'map-marker' },
  { id: 'manual', name: 'Manual Entry', icon: 'keyboard' },
];

export default function CatchReportScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const dispatch = useDispatch<AppDispatch>();
  const catchReports = useSelector((state: RootState) => state.fishing.catchReports || []);
  const { user } = useAuth();
  
  const [selectedSpecies, setSelectedSpecies] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [currentLocation, setCurrentLocation] = useState<GeoCoordinate | null>(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [photoDate, setPhotoDate] = useState<Date | null>(null);
  const [hasPhotoLocation, setHasPhotoLocation] = useState(false);
  const [manualLatitude, setManualLatitude] = useState('');
  const [manualLongitude, setManualLongitude] = useState('');
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapSelectedLocation, setMapSelectedLocation] = useState<GeoCoordinate | null>(null);
  const [showWaypoints, setShowWaypoints] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [visibleWaypoints, setVisibleWaypoints] = useState<any[]>([]);
  const [loadingWaypoints, setLoadingWaypoints] = useState(false);

  // Get current location on mount - removed automatic location fetching
  useEffect(() => {
    // No longer automatically get location - user must select on map
  }, [selectedLocation, hasPhotoLocation]);

  // Load waypoints when toggled on
  useEffect(() => {
    if (showWaypoints && visibleWaypoints.length === 0) {
      loadNearbyWaypoints();
    }
  }, [showWaypoints]);

  const loadNearbyWaypoints = async () => {
    setLoadingWaypoints(true);
    try {
      // Get the center point of the map (use current location or default)
      const centerLat = mapSelectedLocation?.latitude || currentLocation?.latitude || 27.5;
      const centerLng = mapSelectedLocation?.longitude || currentLocation?.longitude || -97.0;
      
      // Load waypoints near the current view (within 50nm radius)
      const nearbyWaypoints = await waypointService.getNearbyWaypoints(
        centerLat,
        centerLng,
        50, // radius in nautical miles
        100 // limit to 100 waypoints
      );
      
      setVisibleWaypoints(nearbyWaypoints);
    } catch (error) {
      console.error('Error loading waypoints:', error);
    } finally {
      setLoadingWaypoints(false);
    }
  };

  // Load user's catch reports from Firebase
  useEffect(() => {
    if (!user) return;

    let unsubscribe: (() => void) | undefined;

    const loadReports = async () => {
      try {
        // Set up real-time listener for user's catch reports
        unsubscribe = catchReportService.listenToUserReports(user.uid, (reports) => {
          // Update Redux store with Firebase data
          dispatch(setCatchReports(reports));
        });
      } catch (error) {
        console.error('Error loading catch reports:', error);
      }
    };

    loadReports();

    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, dispatch]);

  const getCurrentLocation = async () => {
    setLoadingLocation(true);
    try {
      const location = await locationService.getCurrentLocation();
      if (location) {
        setCurrentLocation({
          latitude: location.latitude,
          longitude: location.longitude,
        });
      }
    } catch (error) {
      console.error('Error getting location:', error);
    } finally {
      setLoadingLocation(false);
    }
  };

  const saveCatch = async () => {
    if (!selectedSpecies) {
      Alert.alert('Select Species', 'Please select the species you caught');
      return;
    }

    if (!selectedLocation) {
      Alert.alert('Select Location', 'Please select where you caught the fish');
      return;
    }

    if (!user) {
      Alert.alert('Authentication Required', 'Please log in to save catch reports');
      return;
    }

    setSaving(true);
    try {
      // Determine the location based on selection type
      let reportLocation: GeoCoordinate;
      
      if (selectedLocation === 'manual') {
        // Validate manual coordinates
        const lat = parseFloat(manualLatitude);
        const lon = parseFloat(manualLongitude);
        
        if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          Alert.alert('Invalid Coordinates', 'Please enter valid latitude (-90 to 90) and longitude (-180 to 180)');
          setSaving(false);
          return;
        }
        
        reportLocation = { latitude: lat, longitude: lon };
      } else if ((selectedLocation === 'map' || selectedLocation === 'current') && mapSelectedLocation) {
        reportLocation = mapSelectedLocation;
      } else if (selectedLocation === 'current' && !mapSelectedLocation) {
        Alert.alert('Select Location', 'Please click on the map to mark your catch location');
        setSaving(false);
        setShowMapModal(true);
        return;
      } else {
        reportLocation = currentLocation || { latitude: 0, longitude: 0 };
      }
      
      // Create catch report object
      const catchReportData = {
        userId: user.uid,
        location: reportLocation,
        species: selectedSpecies as FishSpecies,
        count: 1,
        averageSize: 0, // Could add size input later
        conditions: {
          waterTemp: 0, // Could fetch from weather data
          weather: '',
          windSpeed: 0,
          waveHeight: 0,
        },
        method: 'fishing',
        depth: 0,
        notes: notes || undefined,
        // Use selected date
        timestamp: selectedDate.toISOString(),
      };
      
      // Save to Firebase (the listener will update Redux automatically)
      const savedReport = await catchReportService.saveCatchReport(catchReportData, user.uid);
      
      Alert.alert(
        'Catch Logged!', 
        `Your ${FISH_SPECIES.find(s => s.id === selectedSpecies)?.name || 'catch'} has been recorded.`,
        [
          { text: 'Log Another', onPress: () => resetForm() },
          { text: 'Done', onPress: () => navigation.goBack() },
        ]
      );
    } catch (error) {
      Alert.alert('Error', 'Failed to save catch. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedSpecies('');
    setNotes('');
    setSelectedImage(null);
    setPhotoDate(null);
    setHasPhotoLocation(false);
    setManualLatitude('');
    setManualLongitude('');
    setMapSelectedLocation(null);
    setSelectedDate(new Date());
    setSelectedLocation('');
  };

  const pickImageFromGallery = async () => {
    try {
      // Request permission to access media library
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Please grant permission to access your photos.');
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: 'images',
        allowsEditing: false,
        quality: 0.8,
        exif: true, // Request EXIF data
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        setSelectedImage(asset.uri);

        // Extract location from EXIF data
        if (asset.exif) {
          // Check for GPS coordinates
          if (asset.exif.GPSLatitude && asset.exif.GPSLongitude) {
            const latitude = asset.exif.GPSLatitude;
            let longitude = asset.exif.GPSLongitude;
            
            // Fix longitude for Western hemisphere (Americas)
            // GPS coordinates from photos might also have positive values
            if (longitude > 0 && longitude < 180) {
              longitude = -longitude;
            }
            
            // Set location from photo
            setCurrentLocation({ latitude, longitude });
            setSelectedLocation('photo');
            setHasPhotoLocation(true);
            
            // Extract date from EXIF
            let dateMessage = '';
            if (asset.exif.DateTimeOriginal || asset.exif.DateTime) {
              const dateString = asset.exif.DateTimeOriginal || asset.exif.DateTime;
              // EXIF date format is usually "YYYY:MM:DD HH:MM:SS"
              const dateParts = dateString.split(' ');
              if (dateParts.length >= 1) {
                const date = dateParts[0].replace(/:/g, '-');
                const time = dateParts[1] || '00:00:00';
                const photoDateTime = new Date(`${date}T${time}`);
                setPhotoDate(photoDateTime);
                setSelectedDate(photoDateTime);
                
                const formattedDate = photoDateTime.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
                dateMessage = `\nDate: ${formattedDate}`;
              }
            }
            
            Alert.alert(
              'Photo Data Extracted!', 
              `Location: ${latitude.toFixed(4)}°, ${longitude.toFixed(4)}°${dateMessage}`
            );
          } else {
            // No location in photo, but check for date
            if (asset.exif.DateTimeOriginal || asset.exif.DateTime) {
              const dateString = asset.exif.DateTimeOriginal || asset.exif.DateTime;
              const dateParts = dateString.split(' ');
              if (dateParts.length >= 1) {
                const date = dateParts[0].replace(/:/g, '-');
                const time = dateParts[1] || '00:00:00';
                const photoDateTime = new Date(`${date}T${time}`);
                setPhotoDate(photoDateTime);
                setSelectedDate(photoDateTime);
                
                const formattedDate = photoDateTime.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
                
                Alert.alert(
                  'Photo Date Found!', 
                  `This photo was taken on ${formattedDate}`
                );
              }
            }
          }
        } else if (asset.assetId) {
          // Try to get asset info from media library only if we have an assetId
          try {
            // Request MediaLibrary permissions first
            const mediaLibraryStatus = await MediaLibrary.requestPermissionsAsync();
            if (mediaLibraryStatus.status === 'granted') {
              const assetInfo = await MediaLibrary.getAssetInfoAsync(asset.assetId);
              if (assetInfo.location) {
                let longitude = assetInfo.location.longitude;
                // Fix longitude for Western hemisphere
                if (longitude > 0 && longitude < 180) {
                  longitude = -longitude;
                }
                setCurrentLocation({
                  latitude: assetInfo.location.latitude,
                  longitude,
                });
                setSelectedLocation('photo');
                setHasPhotoLocation(true);
              }
              if (assetInfo.creationTime) {
                setPhotoDate(new Date(assetInfo.creationTime));
              }
            }
          } catch (error) {
            console.log('Could not get asset info:', error);
            // Fallback to just using the image without location data
          }
        }
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Toggle between log and history */}
      <View style={styles.toggleContainer}>
        <Button
          mode={!showHistory ? 'contained' : 'outlined'}
          onPress={() => setShowHistory(false)}
          style={styles.toggleButton}
          compact
        >
          Log New Catch
        </Button>
        <Button
          mode={showHistory ? 'contained' : 'outlined'}
          onPress={() => setShowHistory(true)}
          style={styles.toggleButton}
          compact
        >
          Catch History ({catchReports.length})
        </Button>
      </View>

      {!showHistory ? (
        <>
      {/* Photo Upload Option */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.stepTitle}>Add a photo (optional)</Title>
          <View style={styles.photoSection}>
            {selectedImage ? (
              <View style={styles.photoContainer}>
                <Image source={{ uri: selectedImage }} style={styles.photoThumbnail} />
                <View style={styles.photoInfo}>
                  {photoDate && (
                    <Paragraph style={styles.photoDate}>
                      Date: {photoDate.toLocaleDateString()}
                    </Paragraph>
                  )}
                  {selectedLocation === 'photo' && currentLocation && (
                    <Paragraph style={styles.photoLocation}>
                      Location: {currentLocation.latitude.toFixed(4)}°, {currentLocation.longitude.toFixed(4)}°
                    </Paragraph>
                  )}
                  <Button 
                    mode="text" 
                    onPress={() => {
                      setSelectedImage(null);
                      setPhotoDate(null);
                      setHasPhotoLocation(false);
                      if (selectedLocation === 'photo') {
                        setSelectedLocation('current');
                        setCurrentLocation(null);
                      }
                    }}
                    compact
                  >
                    Remove Photo
                  </Button>
                </View>
              </View>
            ) : (
              <Button
                mode="contained-tonal"
                icon="camera"
                onPress={pickImageFromGallery}
                style={styles.photoButton}
              >
                Choose from Gallery
              </Button>
            )}
          </View>
        </Card.Content>
      </Card>

      {/* Step 1: What did you catch? */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.stepTitle}>What did you catch?</Title>
          <View style={styles.speciesGrid}>
            {FISH_SPECIES.map((species) => (
              <TouchableOpacity
                key={species.id}
                style={[
                  styles.speciesChip,
                  selectedSpecies === species.id && { backgroundColor: theme.colors.primaryContainer },
                ]}
                onPress={() => setSelectedSpecies(species.id)}
              >
                <Paragraph 
                  style={[
                    styles.speciesChipText,
                    selectedSpecies === species.id && { color: theme.colors.primary, fontWeight: '600' }
                  ]}
                >
                  {species.name}
                </Paragraph>
              </TouchableOpacity>
            ))}
          </View>
        </Card.Content>
      </Card>

      {/* Step 2: Where? */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.stepTitle}>Where?</Title>
          {hasPhotoLocation && selectedLocation === 'photo' && (
            <Paragraph style={styles.photoLocationNotice}>
              Using location from photo (cannot be changed)
            </Paragraph>
          )}
          <View style={styles.locationGrid}>
            {QUICK_LOCATIONS.map((location) => (
              <TouchableOpacity
                key={location.id}
                style={[
                  styles.locationChip,
                  selectedLocation === location.id && { backgroundColor: theme.colors.primaryContainer },
                ]}
                onPress={() => {
                  if (location.id === 'photo' && !selectedImage) {
                    pickImageFromGallery();
                  } else if (location.id === 'map' || location.id === 'current') {
                    setSelectedLocation(location.id);
                    setShowMapModal(true);
                  } else if (location.id === 'photo' || !hasPhotoLocation) {
                    // Only allow switching away from photo if no photo location exists
                    setSelectedLocation(location.id);
                  }
                }}
              >
                <MaterialCommunityIcons
                  name={location.icon as any}
                  size={20}
                  color={selectedLocation === location.id ? theme.colors.primary : theme.colors.onSurface}
                />
                <Paragraph
                  style={[
                    styles.locationText,
                    selectedLocation === location.id && { color: theme.colors.primary }
                  ]}
                >
                  {location.name}
                </Paragraph>
              </TouchableOpacity>
            ))}
          </View>
          {selectedLocation === 'current' && mapSelectedLocation && (
            <View style={styles.gpsStatus}>
              <View style={styles.loadingRow}>
                <MaterialIcons name="location-on" size={16} color={theme.colors.primary} />
                <Paragraph style={styles.gpsText}>
                  {mapSelectedLocation.latitude.toFixed(4)}°, {mapSelectedLocation.longitude.toFixed(4)}°
                </Paragraph>
              </View>
              <Button
                mode="text"
                onPress={() => setShowMapModal(true)}
                compact
              >
                Change Location
              </Button>
            </View>
          )}
          {selectedLocation === 'photo' && (
            <View style={styles.gpsStatus}>
              {loadingLocation ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator size="small" />
                  <Paragraph style={styles.gpsText}>Getting location...</Paragraph>
                </View>
              ) : currentLocation ? (
                <>
                  <View style={styles.loadingRow}>
                    <MaterialIcons name="location-on" size={16} color={theme.colors.primary} />
                    <Paragraph style={styles.gpsText}>
                      {currentLocation.latitude.toFixed(4)}°, {currentLocation.longitude.toFixed(4)}°
                    </Paragraph>
                  </View>
                  {photoDate && (
                    <View style={styles.loadingRow}>
                      <MaterialIcons name="access-time" size={16} color={theme.colors.primary} />
                      <Paragraph style={styles.gpsText}>
                        {photoDate.toLocaleDateString()} at {photoDate.toLocaleTimeString()}
                      </Paragraph>
                    </View>
                  )}
                </>
              ) : (
                <Paragraph style={styles.gpsText}>
                  No location data in photo
                </Paragraph>
              )}
            </View>
          )}
          {selectedLocation === 'manual' && (
            <View style={styles.manualCoordinateContainer}>
              <View style={styles.coordinateRow}>
                <View style={styles.coordinateInputContainer}>
                  <Paragraph style={styles.coordinateLabel}>Latitude:</Paragraph>
                  <TextInput
                    style={[
                      styles.coordinateInput,
                      { 
                        backgroundColor: theme.colors.surfaceVariant,
                        color: theme.colors.onSurface,
                      }
                    ]}
                    placeholder="-90 to 90"
                    placeholderTextColor={theme.colors.onSurfaceVariant}
                    value={manualLatitude}
                    onChangeText={setManualLatitude}
                    keyboardType="numbers-and-punctuation"
                    maxLength={10}
                  />
                </View>
                <View style={styles.coordinateInputContainer}>
                  <Paragraph style={styles.coordinateLabel}>Longitude:</Paragraph>
                  <TextInput
                    style={[
                      styles.coordinateInput,
                      { 
                        backgroundColor: theme.colors.surfaceVariant,
                        color: theme.colors.onSurface,
                      }
                    ]}
                    placeholder="-180 to 180"
                    placeholderTextColor={theme.colors.onSurfaceVariant}
                    value={manualLongitude}
                    onChangeText={setManualLongitude}
                    keyboardType="numbers-and-punctuation"
                    maxLength={11}
                  />
                </View>
              </View>
              <Paragraph style={styles.coordinateHelp}>
                Enter decimal degrees (e.g., 27.8006, -97.3964)
              </Paragraph>
            </View>
          )}
          {selectedLocation === 'map' && mapSelectedLocation && (
            <View style={styles.gpsStatus}>
              <View style={styles.loadingRow}>
                <MaterialIcons name="location-on" size={16} color={theme.colors.primary} />
                <Paragraph style={styles.gpsText}>
                  {mapSelectedLocation.latitude.toFixed(4)}°, {mapSelectedLocation.longitude.toFixed(4)}°
                </Paragraph>
              </View>
              <Button
                mode="text"
                onPress={() => setShowMapModal(true)}
                compact
              >
                Change Location
              </Button>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Step 3: When? */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.stepTitle}>When?</Title>
          <TouchableOpacity 
            style={[
              styles.dateButton,
              { backgroundColor: theme.colors.surfaceVariant }
            ]}
            onPress={() => setShowDatePicker(true)}
          >
            <MaterialIcons name="calendar-today" size={20} color={theme.colors.primary} />
            <Paragraph style={styles.dateButtonText}>
              {selectedDate.toLocaleDateString('en-US', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </Paragraph>
          </TouchableOpacity>
          {showDatePicker && (
            <DateTimePicker
              value={selectedDate}
              mode="date"
              display={Platform.OS === 'ios' ? 'spinner' : 'default'}
              onChange={(event, date) => {
                if (Platform.OS === 'android') {
                  setShowDatePicker(false);
                }
                if (date) {
                  setSelectedDate(date);
                }
              }}
              maximumDate={new Date()}
            />
          )}
          {showDatePicker && Platform.OS === 'ios' && (
            <Button
              mode="text"
              onPress={() => setShowDatePicker(false)}
              style={styles.datePickerDone}
            >
              Done
            </Button>
          )}
        </Card.Content>
      </Card>

      {/* Step 4: Quick notes (optional) */}
      <Card style={styles.card}>
        <Card.Content>
          <Title style={styles.stepTitle}>Quick notes (optional)</Title>
          <TextInput
            style={[
              styles.notesInput,
              { 
                backgroundColor: theme.colors.surfaceVariant,
                color: theme.colors.onSurface,
              }
            ]}
            placeholder="Size, bait used, conditions..."
            placeholderTextColor={theme.colors.onSurfaceVariant}
            value={notes}
            onChangeText={setNotes}
            multiline
            numberOfLines={3}
            maxLength={200}
          />
          <Paragraph style={styles.charCount}>{notes.length}/200</Paragraph>
        </Card.Content>
      </Card>

      {/* Save Button */}
      <Button
        mode="contained"
        style={styles.saveButton}
        contentStyle={styles.saveButtonContent}
        onPress={saveCatch}
        loading={saving}
        disabled={!selectedSpecies || saving}
        icon="check"
      >
        Save Catch
      </Button>

      {/* Quick tip */}
      <View style={[styles.tipCard, { backgroundColor: theme.colors.surfaceVariant }]}>
        <MaterialIcons name="lightbulb-outline" size={20} color={theme.colors.primary} />
        <Paragraph style={styles.tipText}>
          Pro tip: You can log a catch in under 5 seconds! Just tap the species and hit save.
        </Paragraph>
      </View>
        </>
      ) : (
        /* Catch History View */
        <Card style={styles.card}>
          <Card.Content>
            <Title>Your Catch History</Title>
            {catchReports.length === 0 ? (
              <Paragraph style={styles.emptyText}>
                No catches logged yet. Start by logging your first catch!
              </Paragraph>
            ) : (
              <View style={styles.historyContainer}>
                {catchReports.map((report) => (
                  <TouchableOpacity
                    key={report.id}
                    style={styles.historyItem}
                    onPress={() => Alert.alert(
                      `${FISH_SPECIES.find(s => s.id === (report.species as string))?.name || report.species}`,
                      `Date: ${new Date(report.timestamp).toLocaleDateString()}\n` +
                      `Location: ${report.location.latitude.toFixed(4)}°, ${report.location.longitude.toFixed(4)}°\n` +
                      `Count: ${report.count} fish\n` +
                      `Method: ${report.method}\n${ 
                      report.notes ? `Notes: ${report.notes}` : ''}`
                    )}
                  >
                    <View style={styles.historyInfo}>
                      <View style={styles.historyHeader}>
                        <Paragraph style={styles.historySpecies}>
                          {FISH_SPECIES.find(s => s.id === (report.species as string))?.name || report.species}
                        </Paragraph>
                        <Paragraph style={styles.historyDate}>
                          {new Date(report.timestamp).toLocaleDateString()}
                        </Paragraph>
                      </View>
                      <Paragraph style={styles.historyDetails}>
                        {report.count} fish • {report.method} • {new Date(report.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Paragraph>
                      {report.notes && (
                        <Paragraph style={styles.historyNotes} numberOfLines={2}>
                          {report.notes}
                        </Paragraph>
                      )}
                    </View>
                    <MaterialIcons name="chevron-right" size={24} color={theme.colors.onSurfaceVariant} />
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </Card.Content>
        </Card>
      )}
      
      {/* Map Selection Modal */}
      <Modal
        visible={showMapModal}
        animationType="slide"
        transparent={false}
        onRequestClose={() => setShowMapModal(false)}
      >
        <SafeAreaView style={styles.mapModalContainer}>
          <View style={styles.mapModalHeader}>
            <Title style={styles.mapModalTitle}>Select Location</Title>
            <View style={styles.mapModalHeaderRight}>
              <Button
                mode="text"
                onPress={() => setShowWaypoints(!showWaypoints)}
                icon={loadingWaypoints ? undefined : (showWaypoints ? "anchor" : "anchor-off")}
                compact
                disabled={loadingWaypoints}
              >
                {loadingWaypoints ? "Loading..." : (showWaypoints ? `Hide (${visibleWaypoints.length})` : "Show")} Waypoints
              </Button>
              <IconButton
                icon="close"
                size={24}
                onPress={() => setShowMapModal(false)}
              />
            </View>
          </View>
          
          <View style={styles.mapView}>
            <PlatformMap
              center={{
                lat: mapSelectedLocation?.latitude || currentLocation?.latitude || 27.8006,
                lng: mapSelectedLocation?.longitude || currentLocation?.longitude || -97.3964
              }}
              zoom={8}
              onMapClick={(lat, lng) => {
                setMapSelectedLocation({ latitude: lat, longitude: lng });
              }}
              markers={[
                // Selected location marker
                ...(mapSelectedLocation ? [{
                  position: { lat: mapSelectedLocation.latitude, lng: mapSelectedLocation.longitude },
                  icon: '🎣',
                  size: [35, 35] as [number, number],
                  title: 'Selected Catch Location'
                }] : []),
                // Waypoints markers
                ...(showWaypoints ? visibleWaypoints.map(waypoint => ({
                  position: { lat: waypoint.latitude, lng: waypoint.longitude },
                  icon: '⚓',
                  size: [25, 25] as [number, number],
                  title: waypoint.name
                })) : [])
              ]}
              overlays={[]}
              enableClustering={showWaypoints}
              baseMapType="ocean"
            />
          </View>
          
          <View style={styles.mapModalFooter}>
            {mapSelectedLocation && (
              <Paragraph style={styles.mapSelectedText}>
                Selected: {mapSelectedLocation.latitude.toFixed(4)}°, {mapSelectedLocation.longitude.toFixed(4)}°
              </Paragraph>
            )}
            <View style={styles.mapModalButtons}>
              <Button
                mode="outlined"
                onPress={() => setShowMapModal(false)}
                style={styles.mapModalButton}
              >
                Cancel
              </Button>
              <Button
                mode="contained"
                onPress={() => {
                  if (mapSelectedLocation) {
                    setShowMapModal(false);
                  } else {
                    Alert.alert('No Location Selected', 'Please tap on the map to select a location');
                  }
                }}
                style={styles.mapModalButton}
                disabled={!mapSelectedLocation}
              >
                Confirm
              </Button>
            </View>
          </View>
        </SafeAreaView>
      </Modal>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  stepTitle: {
    fontSize: 18,
    marginBottom: 12,
  },
  speciesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  speciesChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    marginBottom: 4,
  },
  speciesChipText: {
    fontSize: 14,
  },
  locationGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  locationChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    gap: 6,
  },
  locationText: {
    fontSize: 14,
  },
  gpsStatus: {
    marginTop: 12,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gpsText: {
    fontSize: 12,
    opacity: 0.7,
  },
  notesInput: {
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  charCount: {
    fontSize: 12,
    opacity: 0.6,
    textAlign: 'right',
    marginTop: 4,
  },
  saveButton: {
    marginVertical: 16,
  },
  saveButtonContent: {
    paddingVertical: 8,
  },
  tipCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    gap: 8,
    marginBottom: 32,
  },
  tipText: {
    flex: 1,
    fontSize: 12,
    opacity: 0.8,
  },
  toggleContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    paddingHorizontal: 0,
  },
  toggleButton: {
    flex: 1,
  },
  historyContainer: {
    marginTop: 16,
  },
  historyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  historyInfo: {
    flex: 1,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  historySpecies: {
    fontSize: 16,
    fontWeight: '600',
  },
  historyDate: {
    fontSize: 12,
    opacity: 0.6,
  },
  historyDetails: {
    fontSize: 14,
    opacity: 0.8,
    marginBottom: 4,
  },
  historyNotes: {
    fontSize: 12,
    opacity: 0.6,
    fontStyle: 'italic',
  },
  emptyText: {
    textAlign: 'center',
    opacity: 0.6,
    marginTop: 20,
  },
  photoSection: {
    marginTop: 8,
  },
  photoButton: {
    marginVertical: 8,
  },
  photoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  photoThumbnail: {
    width: 100,
    height: 100,
    borderRadius: 8,
  },
  photoInfo: {
    flex: 1,
  },
  photoDate: {
    fontSize: 14,
    marginBottom: 4,
  },
  photoLocation: {
    fontSize: 12,
    opacity: 0.8,
    marginBottom: 8,
  },
  photoLocationNotice: {
    fontSize: 12,
    fontStyle: 'italic',
    color: '#666',
    marginBottom: 8,
  },
  manualCoordinateContainer: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  coordinateRow: {
    flexDirection: 'row',
    gap: 12,
  },
  coordinateInputContainer: {
    flex: 1,
  },
  coordinateLabel: {
    fontSize: 14,
    marginBottom: 4,
    fontWeight: '500',
  },
  coordinateInput: {
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  coordinateHelp: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 8,
    textAlign: 'center',
  },
  mapModalContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  mapModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  mapModalHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  mapModalTitle: {
    fontSize: 20,
    fontWeight: '600',
  },
  mapView: {
    flex: 1,
  },
  mapModalFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  mapSelectedText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 12,
    opacity: 0.8,
  },
  mapModalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  mapModalButton: {
    flex: 1,
  },
  dateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    gap: 12,
  },
  dateButtonText: {
    fontSize: 16,
    flex: 1,
  },
  datePickerDone: {
    alignSelf: 'flex-end',
    marginTop: 8,
  },
});